import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  useGroups,
  useInvitations,
  useInvite,
  useResendInvitation,
  useRevokeInvitation,
} from '../api/rooms'
import { ApiError } from '../api/client'
import type { InvitationResponse, RoomResponse } from '../api/types'
import { errorMessage, formatUtc } from '../lib/format'

/** Owners invite by email, and see every invitation the room has issued with its state. */
export function Invitations({ room, roomId }: { room: RoomResponse; roomId: string }) {
  const invitations = useInvitations(roomId)
  const canInvite = room.status === 'Draft' || room.status === 'Open'

  return (
    <section className="panel">
      <h2 className="h2">Invitations</h2>
      <p className="small muted">
        An address that already has an account here is added at once. Anyone else gets a single-use
        link (valid for a week) to set up an account; they enrol an authenticator on first sign-in,
        and accept the NDA if the room has one, before they see anything.
      </p>
      {canInvite && <InviteForm roomId={roomId} />}
      {invitations.isError && <p className="error-text small">{errorMessage(invitations.error)}</p>}
      {invitations.data && invitations.data.length > 0 && (
        <InvitationList roomId={roomId} invitations={invitations.data} />
      )}
    </section>
  )
}

const schema = z.object({
  email: z.string().trim().email('Enter an email address').max(320),
  memberRole: z.enum(['Participant', 'Owner']),
  realmRole: z.enum(['reviewer', 'contributor', 'room-owner']),
  accessEnds: z.string(),
  groupId: z.string(),
  message: z.string().trim().max(1000),
})
type Form = z.infer<typeof schema>

function InviteForm({ roomId }: { roomId: string }) {
  const invite = useInvite(roomId)
  const groups = useGroups(roomId)
  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      memberRole: 'Participant',
      realmRole: 'reviewer',
      accessEnds: '',
      groupId: '',
      message: '',
    },
  })
  const memberRole = form.watch('memberRole')
  const realmRole = form.watch('realmRole')
  const serverErrors = invite.error instanceof ApiError ? invite.error.violations : {}
  const [last, setLast] = useState<string | null>(null)

  return (
    <form
      className="form invite-form"
      onSubmit={form.handleSubmit((values) =>
        invite.mutate(
          {
            email: values.email,
            memberRole: values.memberRole,
            realmRole: values.memberRole === 'Owner' ? 'room-owner' : values.realmRole,
            accessEndsUtc: values.accessEnds ? new Date(values.accessEnds).toISOString() : null,
            groupId: values.groupId || null,
            message: values.message || null,
          },
          {
            onSuccess: (r) => {
              setLast(
                r.outcome === 'added'
                  ? `${r.email} already had an account and is a member now.`
                  : r.outcome === 'renewed'
                    ? `A fresh link went to ${r.email}; the earlier one no longer works.`
                    : `An invitation went to ${r.email}.`,
              )
              form.reset()
            },
          },
        ),
      )}
    >
      <div className="invite-grid">
        <label className="field">
          <span>Email</span>
          <input
            {...form.register('email')}
            autoComplete="off"
            placeholder="counsel@bidder.example"
          />
          <span className="field-error">
            {form.formState.errors.email?.message ?? serverErrors.email}
          </span>
        </label>
        <label className="field">
          <span>Room role</span>
          <select {...form.register('memberRole')}>
            <option value="Participant">Participant</option>
            <option value="Owner">Owner</option>
          </select>
        </label>
        <label className="field">
          <span>What they may do</span>
          <select {...form.register('realmRole')} disabled={memberRole === 'Owner'}>
            <option value="reviewer">Reviewer</option>
            <option value="contributor">Contributor</option>
          </select>
          {/* What the choice means sits under the control, not inside it: a closed select cuts a long option off. */}
          <span className="small muted">
            {memberRole === 'Owner'
              ? 'Owners get the room-owner role.'
              : realmRole === 'contributor'
                ? 'Views and downloads per permissions, and may upload where granted.'
                : 'Views and downloads per permissions.'}
          </span>
        </label>
        <label className="field">
          <span>Access ends</span>
          <input type="datetime-local" {...form.register('accessEnds')} />
          <span className="field-error">{serverErrors.accessEndsUtc}</span>
        </label>
        <label className="field">
          <span>Group</span>
          <select {...form.register('groupId')}>
            <option value="">None</option>
            {groups.data?.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field invite-message-field">
          <span>Message (optional, goes in the email)</span>
          <input {...form.register('message')} autoComplete="off" />
        </label>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={invite.isPending}>
          {invite.isPending ? 'Sending…' : 'Invite'}
        </button>
        {invite.isError && !serverErrors.email && (
          <span className="error-text small">{errorMessage(invite.error)}</span>
        )}
        {last && !invite.isError && <span className="small muted">{last}</span>}
      </div>
    </form>
  )
}

function InvitationList({
  roomId,
  invitations,
}: {
  roomId: string
  invitations: InvitationResponse[]
}) {
  const resend = useResendInvitation(roomId)
  const revoke = useRevokeInvitation(roomId)

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Email</th>
          <th>Role</th>
          <th>State</th>
          <th>Sent</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {invitations.map((i) => (
          <tr
            key={i.id}
            className={i.state === 'Revoked' || i.state === 'Expired' ? 'row-expired' : undefined}
          >
            <td>
              {i.email}
              {i.groupName && <div className="small muted">Group: {i.groupName}</div>}
              {i.accessEndsUtc && (
                <div className="small muted">Access ends {formatUtc(i.accessEndsUtc)}</div>
              )}
            </td>
            <td>
              <span className="chip">{i.memberRole}</span>{' '}
              <span className="chip">{i.realmRole}</span>
            </td>
            <td>
              <span
                className={`chip ${i.state === 'Accepted' ? '' : i.state === 'Pending' ? 'chip-warn' : 'chip-bad'}`}
              >
                {i.state}
              </span>
              {i.state === 'Pending' && (
                <div className="small muted">until {formatUtc(i.expiresUtc)}</div>
              )}
              {i.acceptedUtc && <div className="small muted">{formatUtc(i.acceptedUtc)}</div>}
            </td>
            <td className="small muted">
              {i.sendCount > 0 ? `${i.sendCount}× · ${formatUtc(i.lastSentUtc)}` : '—'}
              {i.createdBy && <div>by {i.createdBy}</div>}
            </td>
            <td className="td-actions">
              {(i.state === 'Pending' || i.state === 'Expired') && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={resend.isPending}
                    onClick={() => resend.mutate(i.id)}
                  >
                    Resend
                  </button>{' '}
                  {i.state === 'Pending' && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      disabled={revoke.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Revoke the invitation to ${i.email}? The link stops working.`,
                          )
                        )
                          revoke.mutate(i.id)
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </>
              )}
              {(resend.isError || revoke.isError) && (
                <div className="error-text small">{errorMessage(resend.error ?? revoke.error)}</div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
